import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarClock, Film, Heart, History, Home, LogOut, Radio, Search, Settings, Smartphone, Tv, Users } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { Logo } from "./Logo";
import { PlaylistSwitcher } from "./PlaylistSwitcher";
import { PlaylistInfoBadge } from "./PlaylistInfoBadge";
import { StreamModeToggle } from "./StreamModeToggle";
import { setStreamMode, useStreamMode } from "../lib/streamMode";
import { BUILD_ID } from "../lib/build";

const items: { label: string; to: string; icon: typeof Radio }[] = [
  { label: "Hub", to: "/hub", icon: Home },
  { label: "Live TV", to: "/livetv", icon: Radio },
  { label: "Guide", to: "/guide", icon: CalendarClock },
  { label: "Movies", to: "/movies", icon: Film },
  { label: "Series", to: "/series", icon: Tv },
  { label: "Catchup", to: "/catchup", icon: History },
  { label: "Favorites", to: "/favorites", icon: Heart },
  { label: "Search", to: "/search", icon: Search },
];

export default function NavigationSidebar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const streamMode = useStreamMode();

  return (
    <motion.aside
      initial={{ x: -240 }}
      animate={{ x: 0 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="fixed left-0 top-0 h-screen w-[240px] bg-canvas border-r border-muted/30 z-40 hidden lg:flex flex-col"
    >
      {/* Logo */}
      <Link to="/hub" className="pt-6 px-6 pb-4 flex items-center justify-center">
        <Logo className="h-12 w-auto" />
      </Link>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-2 no-scrollbar">
        <p className="text-[11px] font-ui font-medium uppercase tracking-[0.06em] text-text-muted px-4 pt-4 pb-2">
          Browse
        </p>
        {items.map((it) => {
          const Icon = it.icon;
          const active =
            location.pathname === it.to ||
            (it.to !== "/" && location.pathname.startsWith(it.to + "/"));
          return (
            <Link
              key={it.to}
              to={it.to}
              className={`w-full flex items-center gap-3.5 px-4 py-2.5 rounded-xl transition-fast ${
                active
                  ? "bg-card-hover text-text-primary border-l-[3px] border-accent-gold"
                  : "text-text-secondary hover:text-text-primary hover:bg-card-hover/40"
              }`}
            >
              <Icon size={18} className={active ? "text-accent-gold" : ""} />
              <span className="text-[13px] font-ui font-medium uppercase tracking-[0.04em]">
                {it.label}
              </span>
            </Link>
          );
        })}

        <p className="text-[11px] font-ui font-medium uppercase tracking-[0.06em] text-text-muted px-4 pt-6 pb-2">
          Account
        </p>
        <Link
          to="/download"
          className={`w-full flex items-center gap-3.5 px-4 py-2.5 rounded-xl transition-fast ${
            location.pathname === "/download"
              ? "bg-card-hover text-text-primary border-l-[3px] border-accent-gold"
              : "text-text-secondary hover:text-text-primary hover:bg-card-hover/40"
          }`}
        >
          <Smartphone size={18} />
          <span className="text-[13px] font-ui font-medium uppercase tracking-[0.04em]">
            Get the Apps
          </span>
        </Link>
        <Link
          to="/settings"
          className={`w-full flex items-center gap-3.5 px-4 py-2.5 rounded-xl transition-fast ${
            location.pathname === "/settings"
              ? "bg-card-hover text-text-primary border-l-[3px] border-accent-gold"
              : "text-text-secondary hover:text-text-primary hover:bg-card-hover/40"
          }`}
        >
          <Settings size={18} />
          <span className="text-[13px] font-ui font-medium uppercase tracking-[0.04em]">
            Settings
          </span>
        </Link>
        {user?.is_admin && (
          <Link
            to="/admin"
            className={`w-full flex items-center gap-3.5 px-4 py-2.5 rounded-xl transition-fast ${
              location.pathname === "/admin"
                ? "bg-card-hover text-text-primary border-l-[3px] border-accent-gold"
                : "text-text-secondary hover:text-text-primary hover:bg-card-hover/40"
            }`}
          >
            <Users size={18} />
            <span className="text-[13px] font-ui font-medium uppercase tracking-[0.04em]">
              Admin
            </span>
          </Link>
        )}
      </nav>

      {/* Bottom: stream mode + playlist switcher + logout + build stamp */}
      <div className="p-3 space-y-2">
        <StreamModeToggle
          value={streamMode}
          onChange={setStreamMode}
          helperText={
            streamMode === "direct"
              ? "Falls back to proxy for HTTP-only streams."
              : "All streams routed through this server."
          }
        />
        <PlaylistSwitcher />
        <PlaylistInfoBadge />
        <div className="flex items-center justify-between gap-2 px-3 py-2 bg-surface/40 rounded-xl">
          <div className="min-w-0">
            <p className="text-[12px] font-ui font-medium text-text-primary truncate">
              {user?.username || "—"}
            </p>
            <p className="text-[10px] font-mono uppercase tracking-[0.04em] text-text-secondary">
              {user?.is_admin ? "Admin" : "Viewer"}
            </p>
          </div>
          <button
            onClick={async () => {
              await logout();
              navigate("/login");
            }}
            className="text-text-secondary hover:text-danger transition-fast p-2"
            title="Sign out"
          >
            <LogOut size={16} />
          </button>
        </div>
        <p className="text-center text-[10px] font-mono uppercase tracking-[0.04em] text-text-muted">
          build {BUILD_ID}
        </p>
      </div>
    </motion.aside>
  );
}
